import * as React from "react";
import { connect } from "react-redux";
import { createValidator } from "not-valid";
import * as moment from "moment";
import { Form, Label } from "semantic-ui-react";

import { FormBaseComponent, SaveDispatchProps, FormState } from "@neworbit/simpleui-forms";
import { Input } from "@neworbit/simpleui-input";
import { Clinic } from "@teamprevent/common/calendar/models";

import { Appointment } from "@teamprevent/common/calendar";
import { ClinicLocationTypes } from "@teamprevent/common/appointment/models";

import { bookIntoClinic } from "../actions";
import { AdHocCalendarAppointmentModel, BookAppointmentTaskListModel, BookClinicAppointmentModel, BookAppointmentErrors } from "../models";

import { BookClinicAppointmentUnconnected } from "./BookClinicAppointment";

interface OwnProps {
    clinic: Appointment;
    bookTask: BookAppointmentTaskListModel;
    bookAppointmentErrors: BookAppointmentErrors;
    ref: React.Ref<BookClinicAppointmentUnconnected>;
}

interface PropsFromDispatch {
    save: (bookTask: BookAppointmentTaskListModel, model: BookClinicAppointmentModel, clinic: Appointment) => Promise<void>;
}

export class BookClinicAppointmentFormUnconnected extends
    FormBaseComponent<BookClinicAppointmentModel, OwnProps, FormState<BookClinicAppointmentModel> & { showConflictErrors?: boolean }> {

    private withinClinicTimeValidator = createValidator<moment.Moment>(
        date => date && date.utc(true).isBetween(this.props.clinic.start, this.props.clinic.end, null, "[]"),
        "Must be within clinic time"
    );

    private notInPastValidator = createValidator((value: moment.Moment) => value && value.isSameOrAfter(moment()), "Must not be in the past");

    constructor(props: OwnProps & SaveDispatchProps<BookClinicAppointmentModel>) {
        super(props);
        const { start, end } = props.clinic || { start: null, end: null };
        this.state = {
            values: {
                description: "",
                start,
                end
            },
            valid: {}
        };
        this.withinClinicTimeValidator = this.withinClinicTimeValidator.bind(this);
        this.notInPastValidator = this.notInPastValidator.bind(this);
    }

    public componentDidUpdate(prevProps: OwnProps) {
        if (!prevProps.clinic && this.props.clinic) {
            const { start, end } = this.props.clinic;
            this.setState(prevState => ({
                values: { start, end, description: prevState.values.description },
                valid: { start: true, end: true, description: true }
            }));
        }
    }

    public render() {
        const { locationType, description, start, end } = this.state.values;
        return (
            <Form onSubmit={this.handleSubmit} className="clinic-create-form">
                { this.state.showConflictErrors && this.props.bookAppointmentErrors && this.props.bookAppointmentErrors.appointmentClash &&
                    <Label basic color="red" content="Conflicts with another appointment" />}
                {this.props.bookTask && !this.props.bookTask.locationType && this.props.clinic.clinicLocationTypes.length > 1 &&
                    <Input.Dropdown
                        label="Appointment Type"
                        value={locationType}
                        options={ClinicLocationTypes.map(t => ({ text: t, value: t }))}
                        required
                        onChange={(value, valid) => this.updateProperty("locationType", value, valid)}
                    />
                }
                <Input.Text
                    label="Notes"
                    value={description}
                    showErrors={this.state.showErrors}
                    // eslint-disable-next-line react/jsx-no-bind
                    onChange={(value) => this.updateProperty("description", value, true)}
                />
                <Input.Datetime
                    label="Start"
                    value={start}
                    showErrors={this.state.showErrors}
                    required
                    validation={[this.withinClinicTimeValidator, this.notInPastValidator]}
                    onChange={this.onStartUpdate}
                />
                <Input.Datetime
                    label="End"
                    value={end}
                    showErrors={this.state.showErrors}
                    required
                    validation={[this.withinClinicTimeValidator, this.notInPastValidator]}
                    onChange={this.onEndUpdate}
                />
                {this.state.showErrors && !this.validateStartBeforeEndDate(this.state.values.start, this.state.values.end) &&
                    <Label basic color="red" pointing content="The start time must be before the end" />}
            </Form>
        );
    }

    public submit(): void {
        this.handleSubmit({ preventDefault: (): void => undefined } as any);
        this.setState({ showConflictErrors: true });
    }

    private validateStartBeforeEndDate(start: moment.Moment, end: moment.Moment) {
        return !start || !end || end.diff(start) > 0;
    }

    private onStartUpdate = (value: moment.Moment, valid: boolean) => {
        const utcDate = value && value.utc(true);
        const timesValid = this.validateStartBeforeEndDate(utcDate, this.state.values.end);
        this.updateProperty("start", utcDate, valid && timesValid);
        this.updateProperty("end", this.state.values.end,
            this.withinClinicTimeValidator(this.state.values.end).type === "pass" &&
            this.notInPastValidator(this.state.values.end).type === "pass" &&
            timesValid);
        this.setState({ showConflictErrors: false });
    };

    private onEndUpdate = (value: moment.Moment, valid: boolean) => {
        const utcDate = value && value.utc(true);
        const timesValid = this.validateStartBeforeEndDate(this.state.values.start, utcDate);
        this.updateProperty("end", utcDate, valid && timesValid);
        this.updateProperty("start", this.state.values.start,
            this.withinClinicTimeValidator(this.state.values.start).type === "pass" &&
            this.notInPastValidator(this.state.values.start).type === "pass" &&
            timesValid);
        this.setState({ showConflictErrors: false });
    };
}

const mapDispatchToProps = (dispatch: any): PropsFromDispatch => ({
    save: async (bookTask: BookAppointmentTaskListModel, model: BookClinicAppointmentModel, clinic: Clinic) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, appointments, ...clinicDetails } = clinic;
        const newAppointment = {
            ...clinicDetails,
            parentAppointment: clinic,
            start: model.start,
            end: model.end,
            description: model.description
        };
        await dispatch(bookIntoClinic(bookTask, newAppointment, model.locationType));
    }
});

const mergeProps = (stateProps: AdHocCalendarAppointmentModel, propsFromDispatch: PropsFromDispatch, ownProps: OwnProps):
    SaveDispatchProps<AdHocCalendarAppointmentModel> & OwnProps => ({
    ...ownProps,
    save: (model) => propsFromDispatch.save(ownProps.bookTask, model, ownProps.clinic)
});

export const BookClinicAppointmentForm = connect(null, mapDispatchToProps, mergeProps, { forwardRef: true })(BookClinicAppointmentFormUnconnected);
